import type { Action } from 'redux';
import { UserDropdown } from '@ksyos/design-system';
import { useDispatch } from 'react-redux';
import { ServiceCode } from '@common/enums';
import { PractitionerRole } from '@common/enums/practitionerRole';
import { addTrackEvent } from '@frontend/components/pages/customControls/utils';
import {
    employmentHasAccessToHolterDashboard,
    employmentHasAccessToHomePage,
    organizationHasAccessToFundusNoShows,
} from '@frontend/services/api/accessCheckApiService';
import { fetchSession } from '@frontend/services/api/sessionApiService';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { useFrontendSettings } from '@frontend/utils/hooks/useFrontendSettings';
import type { IOrganization } from '../../../interfaces/organization';
import type { EmploymentSwitch } from '../types/employmentSwitch';
import type { GlobalUserContext } from '../types/globalUserContext';
import type { UserEmployment } from '../types/userEmployment';
import { getDisplayInitials } from '../../../../../common/utils/getDisplayInitials';
import { getDisplayName } from '../../../../../common/utils/getDisplayName';
import { logout } from '../../../../store/authentication/actions';
import { setLanguage } from '../../../../store/language/actions';
import { getSelectedLanguage } from '../../../../translate';
import { changeToEmployment } from '../changeToEmployment';
import { EmploymentItems } from './EmploymentItems';

export type UserNavigationProps = {
    currentOrganization: Pick<IOrganization, 'id' | 'name' | 'suppressEmployeePayoutOverview'>;
    employments: UserEmployment[];
    globalUserContext: GlobalUserContext;
    paymentsUrl: string;
    profileUrl: string;
    setEmploymentSwitch: React.Dispatch<React.SetStateAction<EmploymentSwitch | null>>;
    userInformation: Partial<GlobalUserContext>;
};

export function UserNavigation(props: Readonly<UserNavigationProps>) {
    const abortSignal = useAbortSignal();
    const { frontendSettings } = useFrontendSettings();
    const translate = getSelectedLanguage();
    const dispatch = useDispatch();

    return (
        <UserDropdown
            data-dd-action-name="user-employments-dropdown"
            fullName={getDisplayName(props.userInformation)}
            imageUrl={props.userInformation.profilePictureUrl}
            initials={getDisplayInitials(props.userInformation)}
            organization={props.currentOrganization.name}
        >
            {props.employments.length === 1 &&
                props.employments.map((employment) => {
                    const isSelected = props.globalUserContext.selectedEmploymentId === employment.id;

                    return (
                        (checkHasOrganizationPayoutManagerRole(employment) ||
                            employment.hasActiveService ||
                            employment.organization.isKsyos) && (
                            <EmploymentItems
                                employment={employment}
                                isSelected={isSelected}
                                key={employment.id}
                                organizationId={props.currentOrganization.id}
                            />
                        )
                    );
                })}
            {props.employments.length > 1 &&
                props.employments.map((employment) => {
                    const isSelected = props.globalUserContext.selectedEmploymentId === employment.id;

                    return (
                        employment.organizationIsActive &&
                        (checkHasOrganizationPayoutManagerRole(employment) ||
                            employment.hasActiveService ||
                            employment.organization.isKsyos) && (
                            <UserDropdown.Switcher
                                isSelected={isSelected}
                                key={employment.id}
                                onClick={() => {
                                    handleEmploymentChange(employment);
                                }}
                            >
                                <EmploymentItems
                                    employment={employment}
                                    isSelected={isSelected}
                                    organizationId={props.currentOrganization.id}
                                />
                            </UserDropdown.Switcher>
                        )
                    );
                })}
            <UserDropdown.Divider />
            <UserDropdown.Item
                icon="practitioner"
                to={props.profileUrl}
                variant="link"
            >
                {translate.common.profile}
            </UserDropdown.Item>
            {props.paymentsUrl && (
                <UserDropdown.Item
                    icon="honoraria"
                    to={props.paymentsUrl}
                    variant="link"
                >
                    {translate.finance.payments}
                </UserDropdown.Item>
            )}
            <UserDropdown.Item
                className="automation-logoutButton"
                icon="logout"
                onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    logoutClicked();
                }}
                variant="button"
            >
                {translate.common.logout}
            </UserDropdown.Item>
            {frontendSettings.userLanguageSelectionEnabled && (
                <>
                    <UserDropdown.Divider />
                    <UserDropdown.Item
                        className={props.globalUserContext.currentLanguage === 'nl' ? 'active' : ''}
                        onClick={(e: React.MouseEvent) => {
                            e.preventDefault();
                            changeLanguageClicked('nl');
                        }}
                        variant="button"
                    >
                        Nederlands
                    </UserDropdown.Item>
                    <UserDropdown.Item
                        className={props.globalUserContext.currentLanguage === 'en' ? 'active' : ''}
                        onClick={(e: React.MouseEvent) => {
                            e.preventDefault();
                            changeLanguageClicked('en');
                        }}
                        variant="button"
                    >
                        English
                    </UserDropdown.Item>
                </>
            )}
        </UserDropdown>
    );

    function logoutClicked() {
        addTrackEvent('UserNavigation', 'Logout clicked', 'User logs out manually');
        dispatch(logout() as unknown as Action);
    }

    async function changeLanguageClicked(code: string) {
        // await is necessary when running CS on Windows
        dispatch(setLanguage(code) as unknown as Action);
        window.location.reload();
    }

    // This code will do the following on employment change request:
    // On non special pages reload
    // On special pages (patient page, homepage, holterpage):
    //  - check if permission exists for viewing the page (never for patient)
    //  - reload if permission exists
    //  - if permission does not exists a modal will be shown asking if the user want to navigate
    //      - do nothing on no
    //      - navigates to page where permission does exists home > requests
    async function handleEmploymentChange(employment: UserEmployment) {
        if (props.globalUserContext.selectedEmploymentId === employment.id) {
            return;
        }

        if (employment.organization.isKsyos || props.globalUserContext.isKsyos) {
            props.setEmploymentSwitch({ reason: 'noAccess', redirect: '/home', target: employment });

            return;
        }

        const location = window.location.pathname;

        const contextMap = {
            '(/patients/|/episode|/contactsAndTasks)': 'Patient',
            '/employments/[^/]+/personalSettings': 'Employment',
            '/fundusNoShows': 'FundusNoShows',
            '/holter/dashboard': 'HolterDashboard',
            '/home': 'Home',
            '/organizations/[^/]+/view': 'Organization',
            [`/dashboard/${ServiceCode.BariPre}|/mdoManagement|/groupSessionManagement`]: 'Obesitas',
        };

        let context = null;
        for (const [regex, value] of Object.entries(contextMap)) {
            if (new RegExp(regex).test(location)) {
                context = value;
                break;
            }
        }

        if (!context) {
            return await changeToEmployment(employment.id, abortSignal);
        }

        const hasHomeAccess = await employmentHasAccessToHomePage(employment.id, abortSignal);

        switch (context) {
            case 'Patient':
                return props.setEmploymentSwitch({
                    reason: 'patientContext',
                    redirect: hasHomeAccess ? '/home' : '/requests',
                    target: employment,
                });
            case 'Home':
                if (hasHomeAccess) {
                    return await changeToEmployment(employment.id, abortSignal);
                }

                return props.setEmploymentSwitch({ reason: 'noAccess', redirect: '/requests', target: employment });
            case 'HolterDashboard': {
                const hasHolterAccess = await employmentHasAccessToHolterDashboard(employment.id, abortSignal);

                if (hasHolterAccess) {
                    return await changeToEmployment(employment.id, abortSignal);
                }

                return props.setEmploymentSwitch({
                    reason: 'noAccess',
                    redirect: hasHomeAccess ? '/home' : '/requests',
                    target: employment,
                });
            }
            case 'FundusNoShows': {
                const hasFundusNoShowAccess = await organizationHasAccessToFundusNoShows(
                    employment.organization.id,
                    abortSignal
                );

                if (hasFundusNoShowAccess) {
                    return await changeToEmployment(employment.id, abortSignal);
                } else {
                    return props.setEmploymentSwitch({
                        reason: 'noAccess',
                        redirect: hasHomeAccess ? '/home' : '/requests',
                        target: employment,
                    });
                }
            }
            case 'Employment':
                return await changeToEmployment(employment.id, abortSignal, () =>
                    redirectEmploymentLocation(employment.id)
                );
            case 'Organization': {
                const { id: currentOrganizationId } = props.currentOrganization;

                return await changeToEmployment(employment.id, abortSignal, () =>
                    redirectOrganizationLocation(currentOrganizationId)
                );
            }
            case 'Obesitas':
                return props.setEmploymentSwitch({
                    reason: 'noAccess',
                    redirect: hasHomeAccess ? '/home' : '/requests',
                    target: employment,
                });
            default:
                throw new Error(`Unexpected context value`);
        }
    }

    function redirectEmploymentLocation(newEmploymentId: string) {
        const pathParts = window.location.pathname.split('/');
        let redirectLocation = '';
        pathParts.forEach((part) => {
            if (part) {
                redirectLocation +=
                    '/' + (part === props.globalUserContext.selectedEmploymentId ? newEmploymentId : part);
            }
        });
        window.location.href = redirectLocation;
    }

    async function redirectOrganizationLocation(currentOrganizationId: string) {
        const session = await fetchSession(abortSignal);
        const newOrganizationId = session.organizationId;

        const pathParts = window.location.pathname.split('/');
        let redirectLocation = '';
        pathParts.forEach((part) => {
            if (part) {
                redirectLocation += '/' + (part === currentOrganizationId ? newOrganizationId : part);
            }
        });
        window.location.href = redirectLocation;
    }

    function checkHasOrganizationPayoutManagerRole(employment: UserEmployment) {
        return (
            employment.employmentPractitionerRoles &&
            employment.employmentPractitionerRoles.includes(PractitionerRole.organizationPayoutManager)
        );
    }
}
